<script setup>
/**
 * SzContactsFilterSheet — versão do SzFilterSheet adaptada pra lista de
 * contatos. Mesmo visual/UX, mas com APENAS os filtros que fazem sentido
 * pra um contato (que não tem prioridade, responsável, canal direto nem
 * última mensagem).
 *
 * Seções:
 *   - Etiquetas (multi-select)
 *   - Bloqueados (3-state: Todos / Sim / Não)
 *   - Período (pills + de/até) com radio pra escolher entre `created_at`
 *     e `last_activity_at` como atributo do contato
 *
 * Payload de saída (formato appliedFilter do Chatwoot Contact API):
 *   [{ attributeKey, filterOperator, values, queryOperator }]
 */
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import SzDatePicker from 'dashboard/components/widgets/conversation/solarz/SzDatePicker.vue';

// Helpers de cor pra etiquetas
function hexToBg(hex) {
  if (!hex || !hex.startsWith('#')) return '#F1F1F1';
  // Mesma opacidade que CardLabels usa nos cards da lista: hex + '1A'
  // (0x1A / 0xFF ≈ 0.10). Mantém consistência visual entre filtro e cards.
  return `${hex}1A`;
}
function hexToFg(hex) {
  return hex || '#333';
}

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  initialFilters: { type: Array, default: () => [] },
});
const emit = defineEmits(['update:modelValue', 'apply', 'clear']);

const store = useStore();
const labels = useMapGetter('labels/getLabels');

onMounted(() => {
  if (!labels.value?.length) store.dispatch('labels/get');
});

// ───────── Draft state ─────────
const PERIOD_PRESETS = [
  { key: 'today',     label: 'Hoje'          },
  { key: 'thisMonth', label: 'Este mês'      },
  { key: 'lastMonth', label: 'Mês passado'   },
  { key: 'lastWeek',  label: 'Última semana' },
];
// Atributo de contato usado na filtragem por período.
const PERIOD_ATTR_OPTIONS = [
  { value: 'created_at',       label: 'Criado em'        },
  { value: 'last_activity_at', label: 'Última atividade' },
];
// 3-state pra "Bloqueados": '' (todos), 'true', 'false'.
const BLOCKED_OPTIONS = [
  { value: '',      label: 'Todos'           },
  { value: 'true',  label: 'Bloqueados'      },
  { value: 'false', label: 'Não bloqueados'  },
];

const draftLabels = ref([]);             // [{id, title, color}]
const draftPeriodAttr = ref('created_at');
const draftPeriodPreset = ref(null);
const draftFrom = ref('');               // YYYY-MM-DD
const draftTo = ref('');                 // YYYY-MM-DD
const draftBlocked = ref('');            // '' | 'true' | 'false'

const labelsDropdownOpen = ref(false);
const labelsSearchQuery = ref('');

// ───────── Hidratação dos initialFilters ─────────
function hydrateFromFilters(arr) {
  // reset
  draftLabels.value = [];
  draftFrom.value = '';
  draftTo.value = '';
  draftPeriodAttr.value = 'created_at';
  draftPeriodPreset.value = null;
  draftBlocked.value = '';

  (arr || []).forEach(f => {
    const key = f.attributeKey || f.attribute_key;
    const op = f.filterOperator || f.filter_operator;
    const vals = f.values || [];
    if (key === 'labels') {
      const arrV = Array.isArray(vals) ? vals : [vals];
      arrV.forEach(v => {
        const title = typeof v === 'object' ? (v.id || v.name) : v;
        const l = (labels.value || []).find(x => x.title === title);
        if (l) draftLabels.value.push({ id: l.id, title: l.title, color: l.color });
      });
    } else if (key === 'created_at' || key === 'last_activity_at') {
      draftPeriodAttr.value = key;
      const val = Array.isArray(vals) ? vals[0] : vals;
      if (op === 'is_greater_than') draftFrom.value = (val || '').slice(0, 10);
      else if (op === 'is_less_than') draftTo.value = (val || '').slice(0, 10);
    } else if (key === 'blocked') {
      const val = Array.isArray(vals) ? vals[0] : vals;
      draftBlocked.value = String(val);
    }
  });
  detectPreset();
}
function detectPreset() {
  if (!draftFrom.value || !draftTo.value) return;
  const today = new Date();
  const pad = n => String(n).padStart(2, '0');
  const iso = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const t = iso(today);
  if (draftFrom.value === t && draftTo.value === t) {
    draftPeriodPreset.value = 'today';
    return;
  }
  // outros presets ficariam aqui — manter conservador
}

watch(() => props.modelValue, isOpen => {
  if (isOpen) hydrateFromFilters(props.initialFilters);
});
watch(() => props.initialFilters, v => {
  if (props.modelValue) hydrateFromFilters(v);
}, { deep: true });

// ───────── Helpers ─────────
function toggleLabel(l) {
  const exists = draftLabels.value.find(x => x.id === l.id);
  if (exists) {
    draftLabels.value = draftLabels.value.filter(x => x.id !== l.id);
  } else {
    draftLabels.value = [...draftLabels.value, { id: l.id, title: l.title, color: l.color }];
  }
}
function removeLabel(id) {
  draftLabels.value = draftLabels.value.filter(x => x.id !== id);
}

function setPeriodPreset(key) {
  draftPeriodPreset.value = key;
  const today = new Date();
  const pad = n => String(n).padStart(2, '0');
  const iso = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  if (key === 'today') {
    const t = iso(today);
    draftFrom.value = t;
    draftTo.value = t;
  } else if (key === 'thisMonth') {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    draftFrom.value = iso(start);
    draftTo.value = iso(today);
  } else if (key === 'lastMonth') {
    const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const end = new Date(today.getFullYear(), today.getMonth(), 0);
    draftFrom.value = iso(start);
    draftTo.value = iso(end);
  } else if (key === 'lastWeek') {
    const end = new Date(today);
    end.setDate(today.getDate() - 1);
    const start = new Date(end);
    start.setDate(end.getDate() - 6);
    draftFrom.value = iso(start);
    draftTo.value = iso(end);
  }
}
function onManualDateChange() {
  draftPeriodPreset.value = null;
}

function close() { emit('update:modelValue', false); }

function buildPayload() {
  const payload = [];

  if (draftLabels.value.length > 0) {
    // Backend Contact uses `labels` filter with operator equal_to + array of titles.
    payload.push({
      attributeKey: 'labels',
      filterOperator: 'equal_to',
      values: draftLabels.value.map(l => ({ id: l.title, name: l.title })),
      queryOperator: 'and',
      attributeModel: 'standard',
      inputType: 'multi_select',
      dataType: 'text',
      custom_attribute_type: '',
    });
  }

  if (draftBlocked.value === 'true' || draftBlocked.value === 'false') {
    payload.push({
      attributeKey: 'blocked',
      filterOperator: 'equal_to',
      values: [draftBlocked.value === 'true'],
      queryOperator: 'and',
      attributeModel: 'standard',
      inputType: 'search_select',
      dataType: 'text',
      custom_attribute_type: '',
    });
  }

  // Período usa o attributeKey escolhido (created_at | last_activity_at).
  if (draftFrom.value) {
    payload.push({
      attributeKey: draftPeriodAttr.value,
      filterOperator: 'is_greater_than',
      values: [draftFrom.value],
      queryOperator: 'and',
      attributeModel: 'standard',
      inputType: 'date',
      dataType: 'date',
      custom_attribute_type: '',
    });
  }
  if (draftTo.value) {
    payload.push({
      attributeKey: draftPeriodAttr.value,
      filterOperator: 'is_less_than',
      values: [draftTo.value],
      queryOperator: 'and',
      attributeModel: 'standard',
      inputType: 'date',
      dataType: 'date',
      custom_attribute_type: '',
    });
  }
  return payload;
}

function applyFilters() {
  emit('apply', { filters: buildPayload() });
  close();
}

function clearFilters() {
  draftLabels.value = [];
  draftFrom.value = '';
  draftTo.value = '';
  draftPeriodAttr.value = 'created_at';
  draftPeriodPreset.value = null;
  draftBlocked.value = '';
  emit('clear');
  close();
}

// ───────── Busca nos dropdowns ─────────
const filteredLabels = computed(() => {
  const q = labelsSearchQuery.value.trim().toLowerCase();
  const base = labels.value || [];
  if (!q) return base;
  return base.filter(l => String(l?.title || '').toLowerCase().includes(q));
});

// Único dropdown (etiquetas) — toggle simples.
function toggleDropdown(name) {
  if (name === 'labels') labelsDropdownOpen.value = !labelsDropdownOpen.value;
}

function onClickOutsideDropdowns(e) {
  const t = e.target;
  if (!t.closest('.sz-fs-multi-wrap')) {
    labelsDropdownOpen.value = false;
  }
}
onMounted(() => {
  document.addEventListener('mousedown', onClickOutsideDropdowns);
});
onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onClickOutsideDropdowns);
});
</script>

<template>
  <Transition name="sz-fs">
    <div v-if="modelValue" class="sz-fs" aria-label="Filtros avançados">
      <!-- Header -->
      <div class="sz-fs-header">
        <div
          class="sz-fs-close"
          role="button"
          tabindex="0"
          aria-label="Fechar"
          @click="close"
          @keydown.enter.prevent="close"
          @keydown.space.prevent="close"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </div>
        <span class="sz-fs-title">Filtro</span>
        <div class="sz-fs-header-spacer" />
        <div
          class="sz-fs-funnel"
          role="button"
          tabindex="0"
          aria-label="Fechar filtros"
          @click="close"
          @keydown.enter.prevent="close"
          @keydown.space.prevent="close"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15.625 10.625C15.625 10.7908 15.5592 10.9497 15.4419 11.0669C15.3247 11.1842 15.1658 11.25 15 11.25H5C4.83424 11.25 4.67527 11.1842 4.55806 11.0669C4.44085 10.9497 4.375 10.7908 4.375 10.625C4.375 10.4592 4.44085 10.3003 4.55806 10.1831C4.67527 10.0658 4.83424 10 5 10H15C15.1658 10 15.3247 10.0658 15.4419 10.1831C15.5592 10.3003 15.625 10.4592 15.625 10.625ZM18.125 6.25H1.875C1.70924 6.25 1.55027 6.31585 1.43306 6.43306C1.31585 6.55027 1.25 6.70924 1.25 6.875C1.25 7.04076 1.31585 7.19973 1.43306 7.31694C1.55027 7.43415 1.70924 7.5 1.875 7.5H18.125C18.2908 7.5 18.4497 7.43415 18.5669 7.31694C18.6842 7.19973 18.75 7.04076 18.75 6.875C18.75 6.70924 18.6842 6.55027 18.5669 6.43306C18.4497 6.31585 18.2908 6.25 18.125 6.25ZM11.875 13.75H8.125C7.95924 13.75 7.80027 13.8158 7.68306 13.9331C7.56585 14.0503 7.5 14.2092 7.5 14.375C7.5 14.5408 7.56585 14.6997 7.68306 14.8169C7.80027 14.9342 7.95924 15 8.125 15H11.875C12.0408 15 12.1997 14.9342 12.3169 14.8169C12.4342 14.6997 12.5 14.5408 12.5 14.375C12.5 14.2092 12.4342 14.0503 12.3169 13.9331C12.1997 13.8158 12.0408 13.75 11.875 13.75Z" fill="currentColor"/>
          </svg>
        </div>
      </div>

      <!-- Body -->
      <div class="sz-fs-body">
        <!-- Etiquetas -->
        <section class="sz-fs-section">
          <label class="sz-fs-section-label">Etiquetas</label>
          <div class="sz-fs-multi-wrap">
            <div
              class="sz-fs-multi-field"
              @click="toggleDropdown('labels')"
            >
              <div class="sz-fs-multi-tags">
                <template v-if="draftLabels.length === 0">
                  <span class="sz-fs-placeholder">Selecione as etiquetas</span>
                </template>
                <template v-else>
                  <span
                    v-for="l in draftLabels.slice(0, 3)"
                    :key="l.id"
                    class="sz-fs-tag-label"
                    :style="{
                      background: hexToBg(l.color),
                      color: hexToFg(l.color),
                    }"
                    @click.stop
                  >
                    <span class="sz-fs-tag-dot" :style="{ background: l.color || '#666' }" />
                    <span class="sz-fs-tag-text">{{ l.title }}</span>
                    <span class="sz-fs-tag-x" @click.stop="removeLabel(l.id)">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </span>
                  </span>
                  <span v-if="draftLabels.length > 3" class="sz-fs-tag-overflow">
                    +{{ draftLabels.length - 3 }}
                  </span>
                </template>
              </div>
              <svg class="sz-fs-multi-chevron" :class="{ open: labelsDropdownOpen }" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="6 9 12 15 18 9"/></svg>
            </div>
            <div v-if="labelsDropdownOpen" class="sz-fs-multi-dropdown sz-fs-multi-dropdown--search" @click.stop>
              <!-- Search (Sadraque #237 parity) -->
              <div class="sz-fs-dd-search">
                <svg class="sz-fs-dd-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input
                  v-model="labelsSearchQuery"
                  type="text"
                  class="sz-fs-dd-search-input"
                  placeholder="Pesquisar etiquetas"
                />
              </div>
              <div class="sz-fs-dd-scroll">
                <div
                  v-for="l in filteredLabels"
                  :key="l.id"
                  class="sz-fs-multi-option"
                  :class="{ 'sz-fs-multi-option--on': !!draftLabels.find(x => x.id === l.id) }"
                  @click="toggleLabel(l)"
                >
                  <span class="sz-fs-option-dot" :style="{ background: l.color || '#666' }" />
                  <span class="sz-fs-option-text">{{ l.title }}</span>
                  <svg v-if="draftLabels.find(x => x.id === l.id)" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#388E3C" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <div v-if="!filteredLabels.length" class="sz-fs-multi-empty">Sem etiquetas</div>
              </div>
              <div class="sz-fs-dd-gradient" />
            </div>
          </div>
        </section>

        <!-- Bloqueados (3-state) — pill segmented igual ao "Filtrar por" -->
        <section class="sz-fs-section">
          <label class="sz-fs-section-label">Bloqueados</label>
          <div class="sz-fs-segmented">
            <div
              v-for="o in BLOCKED_OPTIONS"
              :key="o.value || 'all'"
              role="tab"
              tabindex="0"
              class="sz-fs-segmented-item"
              :class="{ 'sz-fs-segmented-item--on': draftBlocked === o.value }"
              @click="draftBlocked = o.value"
              @keydown.enter.prevent="draftBlocked = o.value"
              @keydown.space.prevent="draftBlocked = o.value"
            ><span>{{ o.label }}</span></div>
          </div>
        </section>

        <!-- Período -->
        <section class="sz-fs-section">
          <label class="sz-fs-section-label">Período</label>
          <!-- Radio: qual atributo usar pra filtrar por data -->
          <div class="sz-fs-segmented" style="margin-bottom: 8px;">
            <div
              v-for="o in PERIOD_ATTR_OPTIONS"
              :key="o.value"
              role="tab"
              tabindex="0"
              class="sz-fs-segmented-item"
              :class="{ 'sz-fs-segmented-item--on': draftPeriodAttr === o.value }"
              @click="draftPeriodAttr = o.value"
              @keydown.enter.prevent="draftPeriodAttr = o.value"
              @keydown.space.prevent="draftPeriodAttr = o.value"
            ><span>{{ o.label }}</span></div>
          </div>
          <div class="sz-fs-period-presets">
            <div
              v-for="p in PERIOD_PRESETS"
              :key="p.key"
              class="sz-fs-preset"
              :class="{ 'sz-fs-preset--on': draftPeriodPreset === p.key }"
              role="button"
              tabindex="0"
              @click="setPeriodPreset(p.key)"
              @keydown.enter.prevent="setPeriodPreset(p.key)"
              @keydown.space.prevent="setPeriodPreset(p.key)"
            >{{ p.label }}</div>
          </div>
          <div class="sz-fs-date-row">
            <div class="sz-fs-date-field">
              <span class="sz-fs-date-prefix">Início:</span>
              <SzDatePicker
                v-model="draftFrom"
                :max="draftTo || ''"
                placeholder="dd/mm/aaaa"
                @update:modelValue="onManualDateChange"
              />
            </div>
            <div class="sz-fs-date-field">
              <span class="sz-fs-date-prefix">Fim:</span>
              <SzDatePicker
                v-model="draftTo"
                :min="draftFrom || ''"
                placeholder="dd/mm/aaaa"
                @update:modelValue="onManualDateChange"
              />
            </div>
          </div>
        </section>

        <!-- Footer -->
        <div class="sz-fs-footer">
          <button type="button" class="sz-fs-btn sz-fs-btn--ghost" @click="clearFilters">Limpar filtros</button>
          <button type="button" class="sz-fs-btn sz-fs-btn--primary" @click="applyFilters">Filtrar</button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
/* Container — absolute cobrindo header + chips (top:0). z-index abaixo da pill de colapsar sidebar (z>=20 do Chatwoot) */
.sz-fs {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  z-index: 10;
  background: #FFFFFF;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  box-shadow: 0px 4px 10px rgba(15, 23, 42, 0.1);
  font-family: 'Inter', system-ui, sans-serif;
  overflow: hidden;
}

/* Header da sheet */
.sz-fs-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  height: 56px;
  border-bottom: 1px solid #E5E7EB;
  background: #FFFFFF;
  box-sizing: border-box;
}
/* X usa <div>+role="button" pra escapar do reset global de button do Chatwoot */
.sz-fs-close {
  width: 20px; height: 20px;
  display: flex; align-items: center; justify-content: center;
  color: #000000; cursor: pointer;
  background: transparent;
  user-select: none;
}
.sz-fs-close:hover { opacity: 0.7; }
.sz-fs-title {
  font-size: 16px; font-weight: 600; line-height: 24px;
  color: #000000;
}
.sz-fs-header-spacer { flex: 1; }
/* Funnel usa <div> + bg #F0F0F0 (estilo Figma) */
.sz-fs-funnel {
  width: 32px; height: 32px;
  background: #F0F0F0;
  border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  color: #000000; cursor: pointer;
  user-select: none;
}
.sz-fs-funnel:hover { background: #E5E5E5; }

/* Body */
.sz-fs-body {
  display: flex;
  flex-direction: column;
  padding: 12px;
  gap: 16px;
}
.sz-fs-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.sz-fs-section-label {
  font-size: 14px; font-weight: 500; line-height: 20px;
  color: #262626;
}

/* Segmented "Filtrar por" */
.sz-fs-segmented {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 4px;
  background: #F1F1F1;
  border-radius: 8px;
  box-sizing: border-box;
  align-self: flex-start;
}
.sz-fs-segmented-item {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  padding: 0 12px;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s;
}
.sz-fs-segmented-item span {
  font-size: 14px; font-weight: 500; color: #5C5C5C;
}
.sz-fs-segmented-item--on {
  background: #FFFFFF;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}
.sz-fs-segmented-item--on span { color: #3F3F3F; }

/* Prioridade chips */
.sz-fs-prio-row { display: flex; flex-wrap: wrap; gap: 4px; }
.sz-fs-prio {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  padding: 0 4px 0 8px;
  background: #FFFFFF;
  border: 1px solid #E5E5E5;
  border-radius: 999px;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s, border-color 0.15s;
}
.sz-fs-prio:hover { background: rgba(56,142,60,0.04); }
.sz-fs-prio--on {
  background: #DFEFE3;
  border-color: transparent;
}
.sz-fs-prio--on:hover { background: #D2E7D6; }
.sz-fs-prio-label {
  font-size: 12px; font-weight: 500; line-height: 20px; color: #000000;
}
.sz-fs-prio-icon { display: inline-flex; align-items: center; justify-content: center; width: 16px; height: 16px; }

/* Multi-select wrapper (Responsável / Etiquetas) */
.sz-fs-multi-wrap { position: relative; }
.sz-fs-multi-field {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 32px;
  /* Cap altura pra evitar push do conteúdo abaixo quando há muitas tags. */
  max-height: 80px;
  padding: 4px 8px;
  background: #FFFFFF;
  border: 1px solid #D1D5DB;
  border-radius: 8px;
  cursor: pointer;
  box-sizing: border-box;
  overflow: hidden;
}
.sz-fs-multi-tags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
  max-height: 72px;
  overflow-y: auto;
}
/* Scrollbar discreto pra lista de tags quando overflow */
.sz-fs-multi-tags::-webkit-scrollbar { width: 4px; }
.sz-fs-multi-tags::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.18); border-radius: 4px; }
.dark .sz-fs-multi-tags::-webkit-scrollbar-thumb { background: #3E3E43; }
.sz-fs-placeholder { font-size: 14px; color: #6B7280; }
.sz-fs-multi-chevron { color: #6B7280; flex-shrink: 0; transition: transform 0.15s; }
.sz-fs-multi-chevron.open { transform: rotate(180deg); }

/* Tag de agente */
.sz-fs-tag-agent {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 20px;
  padding: 0 4px;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 4px;
  font-size: 12px;
}
.sz-fs-tag-avatar {
  width: 16px; height: 16px; border-radius: 999px; object-fit: cover;
}
.sz-fs-tag-avatar--initial {
  display: inline-flex; align-items: center; justify-content: center;
  background: #FF5500; color: #FFFFFF; font-size: 9px; font-weight: 600;
}
.sz-fs-tag-text {
  font-family: inherit;
  font-size: 12px; font-weight: 400; color: #000000;
  max-width: 100px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
/* X — caixinha cinza com ícone discreto (Sadraque #237) */
.sz-fs-tag-x {
  display: inline-flex; align-items: center; justify-content: center;
  width: 16px; height: 16px;
  padding: 2px;
  background: #E5E5E5;
  border-radius: 4px;
  cursor: pointer;
  color: #6B6B6B;
  flex-shrink: 0;
}
.sz-fs-tag-x:hover { background: #D4D4D8; color: #111; }
.sz-fs-tag-x svg { width: 12px; height: 12px; }

/* Tag de label — alinhada com .sz-clabel de CardLabels.vue (cards da lista) */
.sz-fs-tag-label {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 4px 2px 4px;
  height: 16px;
  border-radius: 29px;
  font-size: 11px;
  font-weight: 500;
  line-height: 100%;
}
.sz-fs-tag-label .sz-fs-tag-text {
  font-size: 11px;
  font-weight: 500;
  color: inherit;
  max-width: 120px;
}
.sz-fs-tag-label .sz-fs-tag-x {
  width: 14px;
  height: 14px;
  background: transparent;
  color: inherit;
  opacity: 0.7;
  border-radius: 999px;
}
.sz-fs-tag-label .sz-fs-tag-x:hover {
  background: rgba(0, 0, 0, 0.08);
  opacity: 1;
}
.sz-fs-tag-label .sz-fs-tag-x svg {
  width: 10px;
  height: 10px;
}
.sz-fs-tag-dot {
  width: 9px; height: 9px; min-width: 9px;
  border-radius: 999px;
  flex-shrink: 0;
}
.sz-fs-tag-overflow {
  display: inline-flex; align-items: center; justify-content: center;
  height: 16px; padding: 0 6px;
  background: #E1EBDC; color: #388E3C;
  border-radius: 24px;
  font-size: 12px; font-weight: 600;
}

/* Dropdown */
.sz-fs-multi-dropdown {
  position: absolute;
  left: 0; right: 0; top: calc(100% + 4px);
  z-index: 60;
  max-height: 220px;
  overflow-y: auto;
  background: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.12);
  padding: 4px;
}
.sz-fs-multi-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;
}
.sz-fs-multi-option:hover { background: rgba(56,142,60,0.06); }
.sz-fs-multi-option--on { background: rgba(56,142,60,0.10); }
.sz-fs-option-avatar {
  width: 24px; height: 24px; border-radius: 999px; object-fit: cover; flex-shrink: 0;
}
.sz-fs-option-avatar--initial {
  display: inline-flex; align-items: center; justify-content: center;
  background: #FF5500; color: #FFFFFF; font-size: 11px; font-weight: 600;
}
.sz-fs-option-dot { width: 10px; height: 10px; border-radius: 999px; flex-shrink: 0; }
.sz-fs-option-text {
  flex: 1; min-width: 0;
  font-size: 13px; color: #000000;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.sz-fs-multi-empty { padding: 8px; font-size: 12px; color: #6B7280; text-align: center; }

/* SZ: dropdown com search (Sadraque #237) — Responsável + Canais
   Estrutura: [search fixo][scroll com items][gradient absoluto na borda] */
.sz-fs-multi-dropdown--search {
  padding: 0;
  max-height: 260px;
  position: relative;
  overflow: hidden;
}
/* SZ: search pill (Figma — "Pesquisar canais")
   Flex row, lupa + input centrados verticalmente. !important blindando
   contra resets globais de <input> do Chatwoot (padding/border/bg). */
.sz-fs-dd-search {
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
  gap: 10px !important;
  margin: 8px 12px !important;
  padding: 8px 12px !important;
  background: #FFFFFF !important;
  border: 1px solid #E5E5E5 !important;
  border-radius: 8px !important;
  box-sizing: border-box !important;
  min-height: 36px !important;
  width: auto !important;
}
.sz-fs-dd-search-icon {
  flex: 0 0 16px !important;
  display: block !important;
  width: 16px !important;
  height: 16px !important;
  color: #9CA3AF !important;
}
.sz-fs-dd-search-input {
  flex: 1 1 auto !important;
  min-width: 0 !important;
  width: 100% !important;
  height: 20px !important;
  line-height: 20px !important;
  padding: 0 !important;
  margin: 0 !important;
  background: transparent !important;
  border: 0 !important;
  outline: 0 !important;
  font-family: 'Inter', system-ui, sans-serif !important;
  font-weight: 500 !important;
  font-size: 14px !important;
  color: #111 !important;
  box-sizing: border-box !important;
  box-shadow: none !important;
  -webkit-appearance: none !important;
  appearance: none !important;
}
.sz-fs-dd-search-input:focus { outline: 0 !important; box-shadow: none !important; border: 0 !important; }
.sz-fs-dd-search-input::placeholder { color: #9CA3AF !important; opacity: 1 !important; }
/* Wrapper de scroll — lista de opções */
.sz-fs-dd-scroll {
  max-height: 208px;
  overflow-y: auto;
  padding: 4px 4px 8px 4px;
}
.sz-fs-dd-scroll::-webkit-scrollbar { width: 8px; }
.sz-fs-dd-scroll::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.18); border-radius: 4px; }
.sz-fs-dd-scroll::-webkit-scrollbar-track { background: transparent; }
.dark .sz-fs-dd-scroll::-webkit-scrollbar-thumb { background: #3E3E43; }
/* Gradient mask absoluto, grudado na borda inferior do dropdown.
   pointer-events:none pra não capturar cliques no último item. */
.sz-fs-dd-gradient {
  position: absolute;
  left: 1px;
  right: 1px;
  bottom: 0;
  height: 36px;
  pointer-events: none;
  background: linear-gradient(180deg, rgba(255,255,255,0) 0%, #FFFFFF 100%);
  border-radius: 0 0 8px 8px;
}
/* Checkbox quadrado (substitui o check icon do lado direito) */
.sz-fs-checkbox {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px; height: 16px;
  background: #FFFFFF;
  border: 1.5px solid #D4D4D8;
  border-radius: 4px;
  flex-shrink: 0;
  color: transparent;
  transition: background 0.12s, border-color 0.12s;
}
.sz-fs-checkbox--on {
  background: #59AA59;
  border-color: #59AA59;
  color: #FFFFFF;
}
.sz-fs-option-text--bold { font-weight: 600; }
.sz-fs-channel-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #9797A0;
  flex-shrink: 0;
}

/* Período */
.sz-fs-period-presets { display: flex; flex-wrap: wrap; gap: 4px; }
.sz-fs-preset {
  display: inline-flex; align-items: center; justify-content: center;
  height: 24px; padding: 0 8px;
  background: #FFFFFF;
  border: 1px solid #E5E5E5;
  border-radius: 999px;
  cursor: pointer; user-select: none;
  font-size: 12px; font-weight: 500; color: #000000;
}
.sz-fs-preset:hover { background: rgba(56,142,60,0.04); }
.sz-fs-preset--on {
  background: #DFEFE3;
  border-color: transparent;
}

.sz-fs-date-row {
  display: flex;
  gap: 8px;
}
.sz-fs-date-field {
  flex: 1; min-width: 0;
  display: flex; flex-direction: column; gap: 4px;
}
.sz-fs-date-prefix {
  font-size: 12px; font-weight: 500; color: #6B7280;
}

/* Footer */
.sz-fs-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 4px;
}
.sz-fs-btn {
  height: 36px;
  padding: 0 16px;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px; font-weight: 500;
  cursor: pointer;
  border: none;
}
.sz-fs-btn--ghost {
  background: #FFFFFF;
  border: 1px solid #D4D4D4;
  color: #000000;
}
.sz-fs-btn--ghost:hover { background: rgba(0,0,0,0.04); }
.sz-fs-btn--primary {
  background: #388E3C;
  color: #FFFFFF;
}
.sz-fs-btn--primary:hover { background: #2F7A33; }

/* Transitions */
.sz-fs-enter-active, .sz-fs-leave-active {
  transition: opacity 0.2s ease-out, transform 0.2s ease-out;
}
.sz-fs-enter-from, .sz-fs-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* ── Dark mode ── */
.dark .sz-fs { background: #1F2125; }
.dark .sz-fs-header { background: #1F2125; border-bottom-color: #2A2C30; }
.dark .sz-fs-close, .dark .sz-fs-title, .dark .sz-fs-section-label { color: #E5E7EB; }
.dark .sz-fs-funnel { background: #2A2C30; color: #E5E7EB; }
.dark .sz-fs-segmented { background: #242528; }
.dark .sz-fs-segmented-item span { color: #9797A0; }
.dark .sz-fs-segmented-item--on { background: #3D3F43; }
.dark .sz-fs-segmented-item--on span { color: #FFFFFF; }
.dark .sz-fs-prio {
  background: transparent; border-color: #3D3F43;
}
.dark .sz-fs-prio-label { color: #E5E7EB; }
.dark .sz-fs-prio--on { background: rgba(56,142,60,0.22); border-color: transparent; }
.dark .sz-fs-multi-field { background: #242528; border-color: #303138; }
.dark .sz-fs-placeholder { color: #92939A; }
.dark .sz-fs-multi-chevron { color: #9CA3AF; }
.dark .sz-fs-tag-text { color: #BBBBBE; }
.dark .sz-fs-tag-agent { background: rgba(255,255,255,0.08); }
/* X — caixinha cinza escura no dark (Sadraque #237) */
.dark .sz-fs-tag-x { background: #333333; color: #9C9C9C; }
.dark .sz-fs-tag-x:hover { background: #404040; color: #E5E5E5; }
.dark .sz-fs-multi-dropdown { background: #151518; border-color: #303134; box-shadow: 0 4px 16px rgba(0,0,0,0.5); }
.dark .sz-fs-multi-option:hover { background: rgba(138,206,138,0.10); }
.dark .sz-fs-multi-option--on { background: rgba(138,206,138,0.18); }
.dark .sz-fs-option-text { color: #9797A0; }
.dark .sz-fs-option-text--bold { color: #E5E7EB; }
/* Search dentro do dropdown — dark */
/* Dark mode: wrapper is transparent, only the bottom divider needs to be
   darker. Input/text colors flip. */
.dark .sz-fs-dd-search {
  background: #1B1B1F !important;
  border-color: #303138 !important;
}
.dark .sz-fs-dd-search-icon { color: rgba(255,255,255,0.4) !important; }
.dark .sz-fs-dd-search-input { color: #E5E7EB !important; }
.dark .sz-fs-dd-search-input::placeholder { color: rgba(255,255,255,0.4) !important; opacity: 1 !important; }
/* Checkbox — dark (Sadraque #237) */
.dark .sz-fs-checkbox { background: #171717; border-color: #525252; }
.dark .sz-fs-checkbox--on { background: #59AA59; border-color: #59AA59; color: #FFFFFF; }
/* Gradient bottom — dark */
.dark .sz-fs-dd-gradient {
  background: linear-gradient(180deg, rgba(21,21,24,0) 0%, #151518 100%);
}
.dark .sz-fs-channel-icon { color: #9797A0; }
.dark .sz-fs-preset { background: transparent; border-color: #3D3F43; color: #E5E7EB; }
.dark .sz-fs-preset--on { background: rgba(56,142,60,0.22); border-color: transparent; color: #FFFFFF; }
.dark .sz-fs-date-prefix { color: #9CA3AF; }
.dark .sz-fs-btn--ghost { background: transparent; border-color: #3E3E43; color: #E5E7EB; }
.dark .sz-fs-btn--ghost:hover { background: rgba(255,255,255,0.05); }
</style>
